import React, { useEffect, useState } from 'react'
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts'

import { milFormate } from '../../utils'
import { Skeleton } from '@wiggui/ds/Skeleton'
import { composeClasses } from '@wiggot/utils'

interface IProps {
  capitalGain: number
  priceIndex: number
  periodCount?: number
  timeSeries: TimeSeries
  gridStroke?: string
  containerClassName?: string
  chartContainerClassName?: string
  chartOnly?: boolean
  isLoading?: boolean
}

interface TimeSeries {
  [year: string]: {
    [month: string]: number
  }
}

export const CapitalGainChart = ({
  isLoading,
  capitalGain,
  priceIndex,
  timeSeries,
  periodCount,
  gridStroke = '#F7F8FC',
  containerClassName = 'min-h-[219px] p-3 rounded-xl border border-[#ECECEC] bg-white',
  chartContainerClassName = 'mt-4 h-[145px]',
  chartOnly
}: IProps) => {
  // statements

  const itemsByPeriod = 4

  // state

  const [history, setHistory] = useState<
    {
      name: string
      value: number
    }[]
  >([])

  // functions

  const loadCapitalGainHistory = async () => {
    const years = Object.entries(timeSeries)

    let prepare: typeof history = []

    let all: [string, number][] = []

    for (const [index, year] of years.entries()) {
      const moths = Object.entries(year[1])

      const yearData: {
        name: string
        value: number
      }[] = []

      all = [
        ...all,
        ...moths.map((month: [string, number]): [string, number] => [
          year[0],
          month[1]
        ])
      ]

      let monthIndex = 0

      // if is the last year, get the last month
      if (index === years.length - 1) {
        monthIndex = moths.length - 1
      }

      yearData.push({
        name: year[0],
        value: moths[monthIndex] ? moths[monthIndex][1] * priceIndex : 0
      })

      const yearFilterd = yearData.filter((year) => year.value !== 0)
      prepare = [...prepare, ...yearFilterd]
    }

    setHistory(prepare)
  }

  // observers

  useEffect(() => {
    if (capitalGain) {
      loadCapitalGainHistory()
    }
  }, [capitalGain, priceIndex])

  return (
    <div>
      <div>
        {isLoading && (
          <div className="w-full min-h-[219px] p-3 rounded-xl border border-[#ECECEC] bg-white ">
            <Esqueleton />
          </div>
        )}

        {!isLoading && Boolean(history.length) && (
          <div className={composeClasses('w-full', containerClassName)}>
            {!chartOnly && (
              <>
                <p className="text-body-s text-[#898CA8]">Plusvalía</p>
                <div className="flex items-center gap-2">
                  <p className="text-xl leading-6 font-bold text-headline-xs text-button-primary">
                    {capitalGain}%
                  </p>
                  <div className="w-4 h-4 flex items-center justify-center rounded-full bg-button-primary">
                    <span
                      translate="no"
                      className="material-symbols-outlined text-surface-white !text-body-l"
                    >
                      arrow_drop_up
                    </span>
                  </div>
                  <p className="text-body-s text-text-secondary">
                    Últimos {periodCount} años
                  </p>
                </div>
              </>
            )}

            <div className={chartContainerClassName}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  width={500}
                  height={145}
                  data={history}
                  margin={{
                    top: 10,
                    right: 10,
                    left: -10,
                    bottom: -10
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />

                  <YAxis
                    domain={[(dataMin: number) => dataMin * 0.9, 'auto']}
                    tickSize={0}
                    minTickGap={5}
                    tickCount={5}
                    interval={0}
                    stroke="#D1D1D1"
                    strokeWidth={0.5}
                    tick={CostomYAxis}
                  />
                  <XAxis
                    dataKey="name"
                    fontSize={12}
                    stroke="#D1D1D1"
                    strokeWidth={0.5}
                    tickSize={0}
                    interval={0}
                    tick={
                      <CostomXAxis
                        seriesCount={history.length}
                        itemsByPeriod={itemsByPeriod}
                      />
                    }
                  />
                  <Tooltip
                    content={CustomTooltip}
                    cursor={{ fill: '#f2f3f6' }}
                  />

                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#0368FE"
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const CostomYAxis = (props: any) => {
  const { x, y, payload, index } = props

  if (index === 0) {
    return <></>
  }

  const value = milFormate({ value: payload.value, compact: true })

  const format = `${value.number}${value.notation}`

  return (
    <g transform={`translate(${x},${y + 8})`}>
      <text x={-4} y={0} textAnchor="end" fill="#7E87A7" fontSize={12}>
        {format}
      </text>
    </g>
  )
}

const CostomXAxis = (props: {
  itemsByPeriod: number
  seriesCount: number
  payload?: {
    index: number
    value: any
  }
  x?: number
  y?: number
}) => {
  const { ...chart } = props
  const isVisible = true
  return isVisible ? (
    <g transform={`translate(${chart.x},${chart.y})`}>
      <text
        x={2}
        y={12}
        textAnchor="end"
        fill="#121212"
        fontSize={12}
        fontWeight={700}
      >
        {chart?.payload?.value}
      </text>
    </g>
  ) : (
    <></>
  )
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const value = milFormate({ value: payload[0].value, compact: true })

    const format = `${value.number}${value.notation}`
    return (
      <div className="py-[3px] px-[8px] rounded bg-[rgba(247,247,247,0.8)]">
        <p className="text-xs leading-[14px]">{format}</p>
      </div>
    )
  }

  return null
}

const Esqueleton = () => {
  return (
    <div className="h-full w-full">
      <div>
        <Skeleton width="w-[112px]" height="h-2" />
        <Skeleton width="w-[64px]" height="h-2" className="mt-2" />
      </div>
      <div className="mt-8 flex gap-2">
        <div className="flex flex-col justify-between">
          <Skeleton width="w-2" height="h-2" />
          <Skeleton width="w-2" height="h-2" />
          <Skeleton width="w-2" height="h-2" />
          <Skeleton width="w-2" height="h-2" />
        </div>
        <div className="flex gap-3 md:gap-6 items-end overflow-hidden">
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[96px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[128px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[96px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[128px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[96px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[128px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[96px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[128px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[96px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[128px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[96px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[128px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[96px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[128px]" />
          <Skeleton width="w-2 md:min-w-4 md:w-4" height="h-[96px]" />
          <Skeleton
            width="hidden md:flex w-2 md:min-w-4 md:w-4"
            height="h-[128px]"
          />
          <Skeleton
            width="hidden md:flex w-2 md:min-w-4 md:w-4"
            height="h-[96px]"
          />
          <Skeleton
            width="hidden md:flex w-2 md:min-w-4 md:w-4"
            height="h-[128px]"
          />
          <Skeleton
            width="hidden md:flex w-2 md:min-w-4 md:w-4"
            height="h-[96px]"
          />
          <Skeleton
            width="hidden lg:flex w-2 md:min-w-4 md:w-4"
            height="h-[128px]"
          />
          <Skeleton
            width="hidden lg:flex w-2 md:min-w-4 md:w-4"
            height="h-[96px]"
          />
          <Skeleton
            width="hidden lg:flex w-2 md:min-w-4 md:w-4"
            height="h-[128px]"
          />
          <Skeleton
            width="hidden lg:flex w-2 md:min-w-4 md:w-4"
            height="h-[96px]"
          />
          <Skeleton
            width="hidden lg:flex w-2 md:min-w-4 md:w-4"
            height="h-[96px]"
          />
          <Skeleton
            width="hidden lg:flex w-2 md:min-w-4 md:w-4"
            height="h-[128px]"
          />
          <Skeleton
            width="hidden lg:flex w-2 md:min-w-4 md:w-4"
            height="h-[96px]"
          />
          <Skeleton
            width="hidden lg:flex w-2 md:min-w-4 md:w-4"
            height="h-[128px]"
          />
          <Skeleton
            width="hidden lg:flex w-2 md:min-w-4 md:w-4"
            height="h-[96px]"
          />
          <Skeleton
            width="hidden lg:flex w-2 md:min-w-4 md:w-4"
            height="h-[128px]"
          />
          <Skeleton
            width="hidden lg:flex w-2 md:min-w-4 md:w-4"
            height="h-[96px]"
          />
        </div>
      </div>
    </div>
  )
}
